import React, { useState, useEffect } from 'react';
import { FieldArray } from 'formik';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Typography,
    FormHelperText,
    Box,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CircularProgress
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FeeScheduleSelection } from '../../feeSchedulePage/feeScheduleSelectionComponent';
import GlobalFeeScheduleFields from './GlobalFeeScheduleFields';
import { PAY_METHOD_PER_UNIT, PAY_METHOD_PERCENTAGE, RULE_APPLY_AT_DATE_OF_SERVICE } from '../../../store/constants/contract';
import { integerEquators } from '../../../store/constants/pages';
import { getFeescheduleRateLabel } from '../../../store/api/feeScheduleItems';
import InfoIcon from '@mui/icons-material/Info';
import { calculateMissingUnits } from '../../../util';
import { DateRangePicker } from '../../../common/dateRangeControl';

// Simple date formatter
const formatDate = (dateString: string | Date): string => {
    if (!dateString) return '';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};


interface RuleFeeScheduleGroupArrayProps {
    values: any;
    setFieldValue: (field: string, value: any) => void;
    setFieldError: (field: string, message: string | undefined) => void;
    errors: any;
    touched: any;
    handleChange: (e: any) => void;
    handleBlur: (e: any) => void;
    payMethods: any[];
    useSplitPayment: boolean;
    useBaseRate: boolean;
    useGlobalFeeSchedules: boolean;
    getFeeScheduleLabels: (feeScheduleId: number, callback: any, labelId: any, setFieldValue: any, useGlobalFeeSchedule?: boolean) => void;
    getFeeSchedule: (feeScheduleId: number, callback: (feeSchedule: any) => void) => void;
    handleHoverFeeSchedule?: (element: HTMLElement, feeScheduleId?: number | null, feeScheduleData?: any,isGlobal?:boolean) => void;
    popoverLeave?: () => void;
    handleGroupPayMethodChange?: (e: any, values: any, setFieldValue: any) => void;
    rateLabelValidationErrors: Record<number, boolean>;
    setRateLabelValidationErrors: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
}

const RuleFeeScheduleGroupArray: React.FC<RuleFeeScheduleGroupArrayProps> = ({
    values,
    setFieldValue,
    setFieldError,
    errors,
    touched,
    handleChange,
    handleBlur,
    payMethods,
    useSplitPayment,
    useBaseRate,
    useGlobalFeeSchedules,
    getFeeScheduleLabels,
    getFeeSchedule,
    handleHoverFeeSchedule,
    popoverLeave,
    handleGroupPayMethodChange,
    rateLabelValidationErrors,
    setRateLabelValidationErrors
}) => {
    // Local state for toggle management
    const [groupFeeScheduleType, setGroupFeeScheduleType] = useState<'local' | 'global'>(
        useGlobalFeeSchedules ? 'global' : 'local'
    );
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingTypeChange, setPendingTypeChange] = useState<'local' | 'global' | null>(null);
    const [validatingRateLabel, setValidatingRateLabel] = useState<Record<number, boolean>>({});
    const [showCustomDatesConfirmDialog, setShowCustomDatesConfirmDialog] = useState(false);
    const [pendingCustomDatesChange, setPendingCustomDatesChange] = useState<{ groupIndex: number; newValue: boolean } | null>(null);

    const handleSplitPayMethodChange = (e) => {
        const payMethodId = e.target.value;
        if (payMethodId && payMethods && payMethods.length > 0) {
            const payMethod = payMethods.find(pm => pm.id === payMethodId);
            if (payMethod && payMethod.name !== PAY_METHOD_PER_UNIT) {
                    setFieldValue('useBaseRate', false);
            }
        }
        handleChange(e);
    };

    // Sync with form value (for edit mode)
    useEffect(() => {
        const expectedType = useGlobalFeeSchedules ? 'global' : 'local';
        if (groupFeeScheduleType !== expectedType) {
            setGroupFeeScheduleType(expectedType);
        }
    }, [useGlobalFeeSchedules, groupFeeScheduleType]);

    // Handle fee schedule selection for a group
    const handleFeeScheduleSelect = (groupIndex: number, rowData: any) => {
        const prefix = `ruleFeeScheduleGroupXref[${groupIndex}]`;

        const isGlobalSchedule = rowData.isGlobal || rowData.isRBRVS;
        const isRBRVSSchedule = Boolean(rowData.isRBRVS);
        const showGlobalConfig = isGlobalSchedule && isRBRVSSchedule;

        // Auto-set toggle based on selected fee schedule type
        if (isGlobalSchedule) {
            setGroupFeeScheduleType('global');
            setFieldValue('useGlobalFeeSchedules', true);
        } else {
            setGroupFeeScheduleType('local');
            setFieldValue('useGlobalFeeSchedules', false);
        }

        if (isGlobalSchedule) {
            setFieldValue(`${prefix}.globalFeeScheduleId`, rowData.id);
            setFieldValue(`${prefix}.globalFeeSchedule`, rowData);
            setFieldValue(`${prefix}.feeScheduleName`, rowData.name);
            setFieldValue(`${prefix}.rateLabels`, rowData.globalFeescheduleLabelToRateXRef || []);
            setFieldValue(`${prefix}.globalFeescheduleLabelToRateXRefId`, null);
            setFieldValue(`${prefix}.feeScheduleId`, null);
            setFieldValue(`${prefix}.feeSchedule`, null);
            setFieldValue(`${prefix}.feescheduleLabelToRateXRefId`, null);
        } else {
            setFieldValue(`${prefix}.feeScheduleId`, rowData.id);
            setFieldValue(`${prefix}.feeScheduleName`, rowData.name);
            setFieldValue(`${prefix}.feeSchedule`, rowData);
            setFieldValue(`${prefix}.feescheduleLabelToRateXRefId`, null);
            // Don't set empty rateLabels here - they will be fetched via getFeeScheduleLabels callback below
            setFieldValue(`${prefix}.globalFeeScheduleId`, null);
            setFieldValue(`${prefix}.globalFeeSchedule`, null);
            setFieldValue(`${prefix}.globalFeescheduleLabelToRateXRefId`, null);
        }

        setFieldValue(`${prefix}.requiresGlobalConfig`, showGlobalConfig);

        // Clear validation error for this group when fee schedule changes
        setRateLabelValidationErrors(prev => ({
            ...prev,
            [groupIndex]: false
        }));

        if (showGlobalConfig) {
            setFieldValue(`${prefix}.selectedRateType`, null);
            setFieldValue(`${prefix}.selectedState`, null);
            setFieldValue(`${prefix}.selectedLocality`, null);
            setFieldValue(`${prefix}.selectedGpci`, null);
            setFieldValue(`${prefix}.global_schedule_rate_column`, null);
        } else {
            setFieldValue(`${prefix}.selectedRateType`, null);
            setFieldValue(`${prefix}.selectedState`, null);
            setFieldValue(`${prefix}.selectedLocality`, null);
            setFieldValue(`${prefix}.selectedGpci`, null);
            setFieldValue(`${prefix}.globalFeescheduleState`, null);
            setFieldValue(`${prefix}.globalFeescheduleLocality`, null);
            setFieldValue(`${prefix}.globalFeescheduleGPCI`, null);
            setFieldValue(`${prefix}.global_schedule_rate_column`, null);
        }

        if (!isGlobalSchedule && getFeeSchedule) {
            getFeeSchedule(rowData.id, (feeSchedule: any) => {
                setFieldValue(`${prefix}.feeSchedule`, feeSchedule);
            });
        }

        if (getFeeScheduleLabels) {
            const rateLabelCallback = (newFeeScheduleLabels: any) => {
                setFieldValue(`${prefix}.rateLabels`, newFeeScheduleLabels);
            };
            getFeeScheduleLabels(rowData.id, rateLabelCallback, null, setFieldValue, isGlobalSchedule);
        }
    };

    const makeFormikPtr = (value: any, setFieldValue: (field: string, value: any) => void, name: string) => ({
        value,
        set: (v: any) => setFieldValue(name, v),
    });

    const handleCustomDatesToggle = (groupIndex: number, newValue: boolean) => {
        const groups = values.ruleFeeScheduleGroupXref || [];
        const group = groups[groupIndex];

        if (groupFeeScheduleType === 'global' && groupIndex === 0 && !newValue && groups.length > 1) {
            setPendingCustomDatesChange({ groupIndex, newValue });
            setShowCustomDatesConfirmDialog(true);
        } else {
            applyCustomDatesToggle(groupIndex, newValue);
        }
    };

    const applyCustomDatesToggle = (groupIndex: number, newValue: boolean) => {
        const prefix = `ruleFeeScheduleGroupXref[${groupIndex}]`;
        setFieldValue(`${prefix}.useCustomDates`, newValue);

        if (newValue) {
            // Initialize with default date range or fee schedule dates
            const group = values.ruleFeeScheduleGroupXref[groupIndex];
            const today = new Date();
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);

            const startDate = group?.feeSchedule?.effectiveDate || today.toISOString().slice(0, 10);
            const endDate = group?.feeSchedule?.expirationDate || tomorrow.toISOString().slice(0, 10);

            setFieldValue(`${prefix}.customDateRange`, {
                startdate: startDate.slice(0, 10),
                enddate: endDate.slice(0, 10),
                relativeFilter: 'custom',
            });
        } else {
            setFieldValue(`${prefix}.customDateRange`, null);
        }
    };

    const handleConfirmCustomDatesChange = () => {
        if (pendingCustomDatesChange) {
            const { groupIndex, newValue } = pendingCustomDatesChange;

            // Remove all groups except the first one
            const firstGroup = values.ruleFeeScheduleGroupXref[0];
            setFieldValue('ruleFeeScheduleGroupXref', [firstGroup]);

            applyCustomDatesToggle(0, newValue);
        }
        setShowCustomDatesConfirmDialog(false);
        setPendingCustomDatesChange(null);
    };

    // Validate date overlaps between groups (only for non-split with multiple groups)
    const validateGroupDateOverlap = (groups: any[]): string | null => {
        if (!groups || groups.length <= 1) return null;
        if (useSplitPayment) return null; // Only validate for non-split with multiple groups

        const overlappingItems = new Set<number>();

        for (let i = 0; i < groups.length; i++) {
            for (let j = i + 1; j < groups.length; j++) {
                const group1 = groups[i];
                const group2 = groups[j];

                // Only check overlaps if both groups have fee schedules selected
                const hasFeeSchedule1 = useGlobalFeeSchedules
                    ? group1.globalFeeScheduleId
                    : group1.feeScheduleId;
                const hasFeeSchedule2 = useGlobalFeeSchedules
                    ? group2.globalFeeScheduleId
                    : group2.feeScheduleId;

                if (!hasFeeSchedule1 || !hasFeeSchedule2) {
                    continue;
                }

                let effectiveDate1: string | undefined;
                let expirationDate1: string | undefined;
                let effectiveDate2: string | undefined;
                let expirationDate2: string | undefined;

                if (group1.useCustomDates && group1.customDateRange) {
                    effectiveDate1 = group1.customDateRange.startdate;
                    expirationDate1 = group1.customDateRange.enddate;
                } else {
                    effectiveDate1 = group1.feeSchedule?.effectiveDate;
                    expirationDate1 = group1.feeSchedule?.expirationDate;
                }

                if (group2.useCustomDates && group2.customDateRange) {
                    effectiveDate2 = group2.customDateRange.startdate;
                    expirationDate2 = group2.customDateRange.enddate;
                } else {
                    effectiveDate2 = group2.feeSchedule?.effectiveDate;
                    expirationDate2 = group2.feeSchedule?.expirationDate;
                }

                if (!effectiveDate1 || !expirationDate1 || !effectiveDate2 || !expirationDate2) {
                    continue;
                }

                const from1 = new Date(effectiveDate1);
                const to1 = new Date(expirationDate1);
                const from2 = new Date(effectiveDate2);
                const to2 = new Date(expirationDate2);

                if (from1 <= to2 && from2 <= to1) {
                    overlappingItems.add(i + 1);
                    overlappingItems.add(j + 1);
                }
            }
        }

        if (overlappingItems.size > 0) {
            const itemNumbers = Array.from(overlappingItems).sort((a, b) => a - b).join(', ');
            return `Fee schedule groups ${itemNumbers} have overlapping date ranges`;
        }

        return null;
    };

    // Handle toggle change with confirmation dialog
    const handleTypeChange = (newType: 'local' | 'global') => {
        const groups = values.ruleFeeScheduleGroupXref || [];
        const hasData = groups.some((g: any) => g.feeScheduleId || g.globalFeeScheduleId);

        if (hasData) {
            setPendingTypeChange(newType);
            setShowConfirmDialog(true);
        } else {
            setGroupFeeScheduleType(newType);
            setFieldValue('useGlobalFeeSchedules', newType === 'global');
        }
    };

    const handleConfirmTypeChange = () => {
        setGroupFeeScheduleType(pendingTypeChange!);
        setFieldValue('useGlobalFeeSchedules', pendingTypeChange === 'global');

        // Reset to one default group
        const defaultGroup = {
            id: null,
            feeScheduleId: null,
            feeScheduleName: '',
            feeSchedule: null,
            feescheduleLabelToRateXRefId: null,
            globalFeeScheduleId: null,
            globalFeescheduleLabelToRateXRefId: null,
            globalFeeSchedule: null,
            globalFeescheduleState: null,
            globalFeescheduleLocality: null,
            globalFeescheduleGPCI: null,
            baseRate: null,
            rateLabels: [],
            requiresGlobalConfig: false,
            selectedState: null,
            selectedLocality: null,
            selectedGpci: null,
            useCustomDates: false,
            customDateRange: null,
            rulePaymentFeeScheduleXRef: [{
                id: null,
                payMethodId: null,
                payValue: null,
                dosMaxRate: null,
                feeScheduleXrefId: null
            }],
            ruleSplitPaymentXRef: [{
                id: null,
                payMethodId: null,
                payValue: null,
                equator: '',
                value: '',
                startsAt: '',
                endsAt: '',
                feeScheduleXrefId: null
            }]
        };

        setFieldValue('ruleFeeScheduleGroupXref', [defaultGroup]);
        setShowConfirmDialog(false);
        setPendingTypeChange(null);
    };

    const groups = values.ruleFeeScheduleGroupXref || [];
    const groupOverlapError = validateGroupDateOverlap(groups);

    // Multiple groups logic:
    // - Local schedules: Always allow multiple groups (with different date ranges)
    // - Global schedules: Allow multiple groups only when first group's useCustomDates is ON
    const notAllowMultipleGroups = useGlobalFeeSchedules && groups.length > 0 && !groups[0]?.useCustomDates;
    const allowMultipleGroups = !notAllowMultipleGroups;

    // Default templates
    const defaultPaymentItem = {
        id: null,
        payMethodId: null,
        payValue: null,
        dosMaxRate: null,
        feeScheduleXrefId: null
    };

    const defaultSplitItem = {
        id: null,
        payMethodId: null,
        payValue: null,
        equator: '',
        value: '',
        startsAt: '',
        endsAt: '',
        feeScheduleXrefId: null
    };

    const defaultGroup = {
        id: null,
        feeScheduleId: null,
        feeScheduleName: '',
        feeSchedule: null,
        feescheduleLabelToRateXRefId: null,
        globalFeeScheduleId: null,
        globalFeescheduleLabelToRateXRefId: null,
        globalFeeSchedule: null,
        globalFeescheduleState: null,
        globalFeescheduleLocality: null,
        globalFeescheduleGPCI: null,
        baseRate: null,
        rateLabels: [],
        requiresGlobalConfig: false,
        selectedState: null,
        selectedLocality: null,
        selectedGpci: null,
        useCustomDates: false,
        customDateRange: null,
        rulePaymentFeeScheduleXRef: [defaultPaymentItem],
        ruleSplitPaymentXRef: [defaultSplitItem]
    };

    const handleCloneGroup = (groupIndex: number, push: (group: any) => void) => {
        const groupToClone = groups[groupIndex];
        if (!groupToClone) return;

        // Deep clone the group and reset all IDs to null
        const clonedGroup = {
            ...groupToClone,
            id: null,
            rulePaymentFeeScheduleXRef: groupToClone.rulePaymentFeeScheduleXRef?.map((item: any) => ({
                ...item,
                id: null,
                feeScheduleXrefId: null
            })) || [defaultPaymentItem],
            ruleSplitPaymentXRef: groupToClone.ruleSplitPaymentXRef?.map((item: any) => ({
                ...item,
                id: null,
                feeScheduleXrefId: null
            })) || [defaultSplitItem]
        };

        push(clonedGroup);
    };

    return (
        <FieldArray
            name="ruleFeeScheduleGroupXref"
            render={({ remove, push }) => (
                <Box>
                    {/* Local/Global Toggle */}
                    {groups.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <Typography variant="body2">Local Fee Schedules</Typography>
                            <Switch
                                size="small"
                                disabled={values.codeSelectionType === "Grouper"}
                                checked={groupFeeScheduleType === 'global'}
                                onChange={(e) => handleTypeChange(e.target.checked ? 'global' : 'local')}
                            />
                            <Typography variant="body2">Global Fee Schedules</Typography>
                        </Box>
                    )}

                    {groups.map((group: any, groupIndex: number) => {
                        const groupFieldName = `ruleFeeScheduleGroupXref[${groupIndex}]`;
                        const groupErrors = errors?.ruleFeeScheduleGroupXref?.[groupIndex];
                        const groupTouched = touched?.ruleFeeScheduleGroupXref?.[groupIndex];

                        return (
                            <Box key={groupIndex} sx={{ mb: groupIndex < groups.length - 1 ? 4 : 0 }}>
                                {/* ===== GROUP HEADER ROW ===== */}
                                <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
                                    {/* Fee Schedule - Always xs={2.75} */}
                                    <Grid item xs={2.75}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel className="MuiInputLabel-shrink" style={{ fontSize: 11, marginTop: -30, marginLeft: 5 }}>
                                                Fee Schedule *
                                            </InputLabel>
                                            <Box
                                                // Only attach hover handlers for LOCAL schedules
                                                onMouseEnter={ (event) =>
                                                        handleHoverFeeSchedule && handleHoverFeeSchedule(
                                                            event.currentTarget,
                                                            group?.feeScheduleId || group?.globalFeeScheduleId,
                                                            group?.feeSchedule || group?.globalFeeSchedule,
                                                            group?.globalFeeScheduleId
                                                        )
                                                }
                                                onMouseLeave={popoverLeave}
                                                onClick={popoverLeave}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiChip-root': {
                                                        maxWidth: '100% !important'
                                                    },
                                                    '& .MuiChip-label': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        display: 'block'
                                                    }
                                                }}
                                                marginTop={1}
                                            >
                                                <FeeScheduleSelection
                                                    value={
                                                        (group?.feeScheduleId || group?.globalFeeScheduleId)
                                                            ? group?.feeScheduleName
                                                            : ''
                                                    }
                                                    defaultValue='Search Fee Schedule'
                                                    feeScheduleType={groupFeeScheduleType === 'global' ? 'global' : 'local'}
                                                    hideTypeToggle={true}
                                                    handleSetFeeSchedule={(rowData: any) =>
                                                        handleFeeScheduleSelect(groupIndex, rowData)
                                                    }
                                                />
                                            </Box>
                                            {groupFeeScheduleType === 'local' && group?.feeSchedule?.effectiveDate && group?.feeSchedule?.expirationDate && (
                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', textDecoration: group?.useCustomDates ? 'line-through' : 'none'}}>
                                                    {formatDate(group.feeSchedule.effectiveDate)} - {formatDate(group.feeSchedule.expirationDate)}
                                                </Typography>
                                            )}
                                            <FormHelperText error>
                                                {groupErrors?.feeScheduleId && groupTouched?.feeScheduleId
                                                    ? groupErrors.feeScheduleId
                                                    : ' '}
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    {(group?.feeScheduleId || group?.globalFeeScheduleId) && (
                                        <>
                                            <Grid item xs={1.75}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5 }}>
                                                    <Switch
                                                        size="small"
                                                        checked={group?.useCustomDates || false}
                                                        onChange={(e) => handleCustomDatesToggle(groupIndex, e.target.checked)}
                                                        disabled={
                                                            groupFeeScheduleType === 'global' &&
                                                            groupIndex > 0 &&
                                                            groups[0]?.useCustomDates
                                                        }
                                                    />
                                                    <Typography variant="body2">Custom Date</Typography>
                                                </Box>
                                            </Grid>

                                            {group?.useCustomDates && (
                                                <Grid item xs={2.75}>
                                                    <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                                                        <DateRangePicker
                                                            disabled={false}
                                                            valuePtr={makeFormikPtr(
                                                                group?.customDateRange,
                                                                setFieldValue,
                                                                `ruleFeeScheduleGroupXref[${groupIndex}].customDateRange`
                                                            )}
                                                            label="Date Range"
                                                            enabledSwitch={false}
                                                            disableDateSection={false}
                                                            disableRelativeFilter={true}
                                                        />
                                                        <FormHelperText error>
                                                            {groupErrors?.customDateRange && groupTouched?.customDateRange
                                                                ? groupErrors.customDateRange
                                                                : ' '}
                                                        </FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                            )}
                                        </>
                                    )}

                                    {/* Rate Label (2 cols) */}
                                    {(group?.feeScheduleId || group?.globalFeeScheduleId) && (
                                        <Grid item xs={1.75}>
                                            <FormControl
                                                fullWidth
                                                size="small"
                                                error={groupErrors?.feescheduleLabelToRateXRefId && groupTouched?.feescheduleLabelToRateXRefId}
                                                sx={{ position: 'relative', mt: 0.5  }}
                                            >
                                                <InputLabel>Rate Label *</InputLabel>
                                                <Select
                                                    size="small"
                                                    name={groupFeeScheduleType === 'global'
                                                        ? `${groupFieldName}.globalFeescheduleLabelToRateXRefId`
                                                        : `${groupFieldName}.feescheduleLabelToRateXRefId`}
                                                    value={(groupFeeScheduleType === 'global'
                                                        ? group?.globalFeescheduleLabelToRateXRefId
                                                        : group?.feescheduleLabelToRateXRefId) || ''}
                                                    onChange={async (e) => {
                                                        handleChange(e);
                                                        // Only validate for LOCAL fee schedules, NOT global
                                                        if (e.target.value && groupFeeScheduleType !== 'global') {
                                                            const feeScheduleId = group.feeScheduleId;
                                                            const labelId = Number(e.target.value);

                                                            // Set loading state
                                                            setValidatingRateLabel(prev => ({
                                                                ...prev,
                                                                [groupIndex]: true
                                                            }));

                                                            try {
                                                                // Call the API directly (not the Redux action)
                                                                const hasNoValue = await getFeescheduleRateLabel(
                                                                    feeScheduleId,
                                                                    labelId
                                                                );

                                                                const fieldName = `ruleFeeScheduleGroupXref.[${groupIndex}].feescheduleLabelToRateXRefId`;

                                                                // Update state to track validation errors
                                                                setRateLabelValidationErrors(prev => ({
                                                                    ...prev,
                                                                    [groupIndex]: hasNoValue
                                                                }));

                                                                if (hasNoValue) {
                                                                    // API returned true = rate has no value = error
                                                                    setFieldError(fieldName, 'This rate label has no value');
                                                                } else {
                                                                    // API returned false = rate has a value = clear error
                                                                    setFieldError(fieldName, undefined);
                                                                }
                                                            } catch (error) {
                                                                // Silently handle validation errors
                                                            } finally {
                                                                // Clear loading state
                                                                setValidatingRateLabel(prev => ({
                                                                    ...prev,
                                                                    [groupIndex]: false
                                                                }));
                                                            }
                                                        } else {
                                                            // Clear validation error for this group
                                                            setRateLabelValidationErrors(prev => ({
                                                                ...prev,
                                                                [groupIndex]: false
                                                            }));
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        handleBlur(e);
                                                        // Re-apply the error if validation state says there's an error
                                                        // This prevents Formik from clearing the error on blur
                                                        if (rateLabelValidationErrors[groupIndex]) {
                                                            const fieldName = `ruleFeeScheduleGroupXref.[${groupIndex}].feescheduleLabelToRateXRefId`;
                                                            setFieldError(fieldName, 'This rate label has no value');
                                                        }
                                                    }}
                                                    label="Rate Label"
                                                >
                                                    <MenuItem value="">
                                                        <em>Select Rate Label</em>
                                                    </MenuItem>
                                                    {group?.rateLabels?.map((label: any) => (
                                                        <MenuItem key={label.id} value={label.id}>
                                                            {label.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {validatingRateLabel[groupIndex] && (
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{
                                                            position: 'absolute',
                                                            right: 32,
                                                            top: '50%',
                                                            marginTop: '-10px',
                                                            zIndex: 1
                                                        }}
                                                    />
                                                )}
                                                <FormHelperText>
                                                    {(groupErrors?.feescheduleLabelToRateXRefId && groupTouched?.feescheduleLabelToRateXRefId)
                                                        ? groupErrors.feescheduleLabelToRateXRefId
                                                        : ' '}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                    )}

                                    {/* Base Rate (2 cols) - Only show when useBaseRate is true and fee schedule selected */}
                                    {useBaseRate && (group?.feeScheduleId || group?.globalFeeScheduleId) ? (
                                        <Grid item xs={1.75}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Base Rate *"
                                                name={`${groupFieldName}.baseRate`}
                                                type="number"
                                                value={group?.baseRate ?? ''}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={groupErrors?.baseRate && groupTouched?.baseRate}
                                                helperText={
                                                    groupErrors?.baseRate && groupTouched?.baseRate
                                                        ? groupErrors.baseRate
                                                        : ' '
                                                }
                                                sx={{ marginTop: 0.5 }}
                                            />
                                        </Grid>
                                    )
                                    : (
                                        <Grid item xs={2}>
                                        </Grid>
                                    )

                                }

                                    {/* Icons */}
                                    <Grid item xs={1} sx={{ textAlign: 'right', mt: 1.5 }}>
                                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                                            {/* Add button - Only show for non-split and only on first group */}
                                            {allowMultipleGroups && groupIndex === 0 && (
                                                <Tooltip title="Add Fee Schedule Group">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => {
                                                            var newGroup = defaultGroup;
                                                            if(useGlobalFeeSchedules){
                                                                const today = new Date();
                                                                const tomorrow = new Date(today);
                                                                tomorrow.setDate(today.getDate() + 1);

                                                                newGroup = {
                                                                    ...newGroup,
                                                                    useCustomDates: true,
                                                                    customDateRange: {
                                                                        startdate: today.toISOString().slice(0, 10),
                                                                        enddate: tomorrow.toISOString().slice(0, 10),
                                                                        relativeFilter: 'custom',
                                                                    }
                                                                }
                                                            }
                                                            push(newGroup);
                                                        }}
                                                        size="small"
                                                    >
                                                        <AddCircleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {/* Clone button - Clone the current group */}
                                            {allowMultipleGroups && (
                                                <Tooltip title="Clone Fee Schedule Group">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleCloneGroup(groupIndex, push)}
                                                        size="small"
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {/* Delete button - Only if multiple groups exist (can't delete last group) */}
                                            {allowMultipleGroups && groups.length > 1 && (
                                                <Tooltip title="Delete Fee Schedule Group">
                                                    <IconButton
                                                        onClick={() => remove(groupIndex)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Global Fee Schedule Regional Configuration */}
                                {groupFeeScheduleType === 'global' && group?.globalFeeScheduleId && group?.requiresGlobalConfig && (
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12}>
                                            <GlobalFeeScheduleFields
                                                scheduleId={group.globalFeeScheduleId}
                                                isRBRVS={group.requiresGlobalConfig}
                                                values={{
                                                    selectedRateType: group.selectedRateType || null,
                                                    selectedState: group.selectedState || null,
                                                    selectedLocality: group.selectedLocality || null,
                                                    selectedGpci: group.selectedGpci || null
                                                }}
                                                onChange={(field, value) => {
                                                    setFieldValue(`${groupFieldName}.${field}`, value);
                                                }}
                                                errors={{
                                                    selectedRateType: (groupTouched?.selectedRateType && groupErrors?.selectedRateType) || '',
                                                    selectedState: (groupTouched?.selectedState && groupErrors?.selectedState) || '',
                                                    selectedLocality: (groupTouched?.selectedLocality && groupErrors?.selectedLocality) || '',
                                                    selectedGpci: (groupTouched?.selectedGpci && groupErrors?.selectedGpci) || ''
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {/* ===== ITEM ROWS (nested under header) ===== */}
                                {!useSplitPayment && (group?.feeScheduleId || group?.globalFeeScheduleId) && (
                                    // NON-SPLIT: Exactly 1 payment row per group (no add/delete)
                                    <Grid container spacing={2} sx={{ pl: 2 }}>
                                        {/* Pay Method */}
                                        <Grid item xs={2}>
                                            <FormControl
                                                fullWidth
                                                size="small"
                                                error={groupErrors?.rulePaymentFeeScheduleXRef?.[0]?.payMethodId && groupTouched?.rulePaymentFeeScheduleXRef?.[0]?.payMethodId}
                                            >
                                                <InputLabel>Pay Method *</InputLabel>
                                                <Select
                                                    size="small"
                                                    name={`${groupFieldName}.rulePaymentFeeScheduleXRef[0].payMethodId`}
                                                    value={group?.rulePaymentFeeScheduleXRef?.[0]?.payMethodId || ''}
                                                    onChange={(e) =>  handleGroupPayMethodChange(e, values, setFieldValue)}
                                                    onBlur={handleBlur}
                                                    label="Pay Method"
                                                >
                                                    <MenuItem value="">
                                                        <em>Select Pay Method</em>
                                                    </MenuItem>
                                                    {payMethods?.filter((method: any) =>
                                                        method.name === PAY_METHOD_PERCENTAGE || method.name === PAY_METHOD_PER_UNIT
                                                    ).map((method: any) => (
                                                        <MenuItem key={method.id} value={method.id}>
                                                            {method.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText error>
                                                    {groupErrors?.rulePaymentFeeScheduleXRef?.[0]?.payMethodId && groupTouched?.rulePaymentFeeScheduleXRef?.[0]?.payMethodId
                                                        ? groupErrors.rulePaymentFeeScheduleXRef[0].payMethodId
                                                        : ' '}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>

                                        {/* Pay Value */}
                                        <Grid item xs={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Pay Value *"
                                                name={`${groupFieldName}.rulePaymentFeeScheduleXRef[0].payValue`}
                                                type="number"
                                                value={group?.rulePaymentFeeScheduleXRef?.[0]?.payValue ?? ''}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={groupErrors?.rulePaymentFeeScheduleXRef?.[0]?.payValue && groupTouched?.rulePaymentFeeScheduleXRef?.[0]?.payValue}
                                                helperText={
                                                    groupErrors?.rulePaymentFeeScheduleXRef?.[0]?.payValue && groupTouched?.rulePaymentFeeScheduleXRef?.[0]?.payValue
                                                        ? groupErrors.rulePaymentFeeScheduleXRef[0].payValue
                                                        : ''
                                                }
                                            />
                                        </Grid>

                                        {/* DOS Max Rate - Conditional, with empty spacer when hidden */}
                                        {values.applyAt === RULE_APPLY_AT_DATE_OF_SERVICE ? (
                                            <Grid item xs={2}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="DOS Max Rate"
                                                    name={`${groupFieldName}.rulePaymentFeeScheduleXRef[0].dosMaxRate`}
                                                    type="number"
                                                    value={group?.rulePaymentFeeScheduleXRef?.[0]?.dosMaxRate ?? ''}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={groupErrors?.rulePaymentFeeScheduleXRef?.[0]?.dosMaxRate && groupTouched?.rulePaymentFeeScheduleXRef?.[0]?.dosMaxRate}
                                                    helperText={
                                                        groupErrors?.rulePaymentFeeScheduleXRef?.[0]?.dosMaxRate && groupTouched?.rulePaymentFeeScheduleXRef?.[0]?.dosMaxRate
                                                            ? groupErrors.rulePaymentFeeScheduleXRef[0].dosMaxRate
                                                            : ''
                                                    }
                                                />
                                            </Grid>
                                        ) : (
                                            <Grid item xs={2} /> // Empty spacer when DOS Max Rate is hidden
                                        )}

                                        {/* Empty spacer - Always present for alignment */}
                                        <Grid item xs={2} />
                                    </Grid>
                                )}

                                {useSplitPayment && (group?.feeScheduleId || group?.globalFeeScheduleId) && (
                                    // SPLIT: Multiple split rows within group (can add/delete)
                                    <FieldArray name={`${groupFieldName}.ruleSplitPaymentXRef`}>
                                        {({ push: pushSplit, remove: removeSplit }) => (
                                            <Box sx={{ pl: 2 }}>
                                                {group.ruleSplitPaymentXRef?.map((split: any, splitIndex: number) => {
                                                    const splitFieldName = `${groupFieldName}.ruleSplitPaymentXRef[${splitIndex}]`;
                                                    const splitErrors = groupErrors?.ruleSplitPaymentXRef?.[splitIndex];
                                                    const splitTouched = groupTouched?.ruleSplitPaymentXRef?.[splitIndex];

                                                    return (
                                                        <Grid container spacing={2} key={splitIndex} sx={{ mb: 1 }}>
                                                            {/* Equator */}
                                                            <Grid item xs={3}>
                                                                <FormControl
                                                                    fullWidth
                                                                    size="small"
                                                                    error={splitErrors?.equator && splitTouched?.equator}
                                                                >
                                                                    <InputLabel>Equator *</InputLabel>
                                                                    <Select
                                                                        size="small"
                                                                        name={`${splitFieldName}.equator`}
                                                                        value={split?.equator || ''}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        label="Equator"
                                                                    >
                                                                        <MenuItem value=""><em>Select</em></MenuItem>
                                                                        {integerEquators.equatorArr.map((equator: any) => (
                                                                            <MenuItem key={equator.value} value={equator.value}>
                                                                                {equator.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                    <FormHelperText error>
                                                                        {splitErrors?.equator && splitTouched?.equator
                                                                            ? splitErrors.equator
                                                                            : ' '}
                                                                    </FormHelperText>
                                                                </FormControl>
                                                            </Grid>

                                                            {/* Value */}
                                                                {split?.equator === 'in_between' ? (
                                                                    <Grid item xs={3}>
                                                                        <Box display="flex" gap={1}>
                                                                            <TextField
                                                                                size="small"
                                                                                label="Start"
                                                                                name={`${splitFieldName}.startsAt`}
                                                                                type="number"
                                                                                value={split?.startsAt ?? ''}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                sx={{ width: '50%' }}
                                                                            />
                                                                            <TextField
                                                                                size="small"
                                                                                label="End"
                                                                                name={`${splitFieldName}.endsAt`}
                                                                                type="number"
                                                                                value={split?.endsAt ?? ''}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                sx={{ width: '50%' }}
                                                                            />
                                                                        </Box>
                                                                    </Grid>
                                                                ) : (
                                                                    <Grid item xs={2}>
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            label="Value *"
                                                                            name={`${splitFieldName}.value`}
                                                                            type="number"
                                                                            value={split?.value ?? ''}
                                                                            onChange={handleChange}
                                                                            onBlur={handleBlur}
                                                                            error={splitErrors?.value && splitTouched?.value}
                                                                            helperText={splitErrors?.value && splitTouched?.value ? splitErrors.value : ''}
                                                                        />
                                                                    </Grid>
                                                                )}

                                                            {/* Pay Method */}
                                                            <Grid item xs={2}>
                                                                <FormControl
                                                                    fullWidth
                                                                    size="small"
                                                                    error={splitErrors?.payMethodId && splitTouched?.payMethodId}
                                                                >
                                                                    <InputLabel>Pay Method *</InputLabel>
                                                                    <Select
                                                                        size="small"
                                                                        name={`${splitFieldName}.payMethodId`}
                                                                        value={split?.payMethodId || ''}
                                                                        onChange={handleSplitPayMethodChange}
                                                                        onBlur={handleBlur}
                                                                        label="Pay Method"
                                                                    >
                                                                        <MenuItem value=""><em>Select</em></MenuItem>
                                                                        {payMethods?.filter((method: any) => method.name === PAY_METHOD_PER_UNIT).map((method: any) => (
                                                                            <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                    <FormHelperText error>
                                                                        {splitErrors?.payMethodId && splitTouched?.payMethodId
                                                                            ? splitErrors.payMethodId
                                                                            : ' '}
                                                                    </FormHelperText>
                                                                </FormControl>
                                                            </Grid>

                                                            {/* Pay Value */}
                                                            <Grid item xs={2}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    label="Pay Value *"
                                                                    name={`${splitFieldName}.payValue`}
                                                                    type="number"
                                                                    value={split?.payValue ?? ''}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    error={splitErrors?.payValue && splitTouched?.payValue}
                                                                    helperText={splitErrors?.payValue && splitTouched?.payValue ? splitErrors.payValue : ''}
                                                                />
                                                            </Grid>

                                                            {/* Icons */}
                                                            <Grid item xs={2}>
                                                                <Box display="flex" gap={0.5}>
                                                                    {/* Add button (only on first row) */}
                                                                    {splitIndex === 0 && (
                                                                        <Tooltip title="Add Split Row">
                                                                            <IconButton
                                                                                color="primary"
                                                                                onClick={() => pushSplit(defaultSplitItem)}
                                                                                size="small"
                                                                            >
                                                                                <AddCircleIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    {/* Delete button (only if more than 1 row) */}
                                                                    {group.ruleSplitPaymentXRef.length > 1 && (
                                                                        <Tooltip title="Delete Split Row">
                                                                            <IconButton
                                                                                onClick={() => removeSplit(splitIndex)}
                                                                                size="small"
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    );
                                                })}

                                                {(() => {
                                                    const groupMissedUnits = calculateMissingUnits(group.ruleSplitPaymentXRef);
                                                    return groupMissedUnits && (
                                                        <Typography color="error" sx={{ p: "20px 0" }}>
                                                            Missing Units: <b>{groupMissedUnits}</b> All missing Units will be treated as 0
                                                        </Typography>
                                                    );
                                                })()}
                                            </Box>
                                        )}
                                    </FieldArray>
                                )}
                            </Box>
                        );
                    })}

                    {/* Group Overlap Error */}
                    {groupOverlapError && (
                        <Box sx={{ mt: 2 }}>
                            <FormHelperText error style={{ fontSize: '0.875rem' }}>
                                {groupOverlapError}
                            </FormHelperText>
                        </Box>
                    )}

                    {useSplitPayment && (
                        <Typography color="error" sx={{ p: "20px" }}>
                            {useSplitPayment && (
                                <>
                                    <InfoIcon /> Note: Missing Units will be treated as 0
                                </>
                            )}
                        </Typography>
                    )}
                    
                    {/* Confirmation Dialog - Fee Schedule Type Change */}
                    <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
                        <DialogTitle>Confirm Fee Schedule Type Change</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Changing fee schedule type will clear all existing entries. Do you want to continue?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowConfirmDialog(false)} color="inherit">
                                Cancel
                            </Button>
                            <Button onClick={handleConfirmTypeChange} color="primary" variant="contained">
                                Confirm
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Confirmation Dialog - Custom Dates Toggle for Global Schedules */}
                    <Dialog open={showCustomDatesConfirmDialog} onClose={() => setShowCustomDatesConfirmDialog(false)}>
                        <DialogTitle>Disable Custom Dates?</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Turning off custom dates for global schedules will remove all additional fee schedule groups. Only one global schedule group will remain. Do you want to continue?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowCustomDatesConfirmDialog(false)} color="inherit">
                                Cancel
                            </Button>
                            <Button onClick={handleConfirmCustomDatesChange} color="primary" variant="contained">
                                Confirm
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            )}
        />
    );
};

export default RuleFeeScheduleGroupArray;
